
import Vue, { CreateElement, VNodeData } from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import { generateCss } from "@/lib/canvas/styles";
import { PropertiesData, TreeNode } from "@/types/tree";
import {
  getDynamicShortcodesFromPropertiesData,
  hashDynamicShortcode,
  loadDynamicData
} from "@/lib/dynamic-data/load";
import lodash from "lodash";
import { applyFilters } from "@/lib/api/api";
import {
  configStore,
  documentStore,
  globalStore,
  uiStore
} from "@/store/store";
import { addBuilderElementDependenciesToHead } from "@/lib/canvas/builder-element-dependencies";
import { htmlToVNodes } from "@/lib/canvas/html-to-vnodes";
import { serverSideRender } from "@/lib/server-side-render/server-side-render";
import { SelectorCSSName } from "@/types/selectors";
import { VNode } from "vue/types/vnode";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";

import EmptyContainer from "@/components/Canvas/Empty/EmptyContainer.vue";
import EmptyColumns from "@/components/Canvas/Empty/EmptyColumns.vue";
import {
  getBuilderElementAdditionalClassTemplateCacheKey,
  getBuilderElementAttributeTemplateCacheKey,
  getBuilderElementCssTemplateCacheKey,
  getBuilderElementTemplateCacheKey,
  renderTags,
  renderTwig
} from "@/lib/twig/twig";
import { breakdanceStoreActions } from "@/store/actions/store-actions";
import {
  getDynamicMetaPath,
  isDynamicShortcode
} from "@/lib/dynamic-data/utils";
import { isObject, objectDeepMap } from "@/util/objects";
import NotificationLogger from "@/lib/notifications/notifications";
import { rerunAllScriptsInCanvasDocumentBody } from "@/lib/server-side-render/rerun-javascripts";
import { BuilderElementAttribute } from "@/types/elements";
import { COLUMNS_SLUG, RICH_TEXT_SLUG } from "@/lib/elements/constants";
import { openUiPanel } from "@/lib/panels/panels";
import { EasyEvent, isCanvasEvent } from "@/util/easy-event";
import {
  emptyHtmlFromSsrMessage,
  requiredPluginsNotAvailableMessage,
  ssrErrorMessage
} from "@/lib/canvas/ssr-html-strings";
import {
  addOrUpdateElementSubStylesComingFromSsr,
  addOrUpdateGeneratedBuilderElementDefaultStylesInlineCSSInCanvasHead,
  addOrUpdateGeneratedBuilderElementStylesInlineCSSInCanvasHead,
  removeElementDefaultStylesFromCanvasHead,
  removeElementStylesCameFromSsrFromCanvasHead,
  removeElementStylesFromCanvasHead
} from "@/lib/canvas/assets/css-in-head";
import { countNodesOfTypeInTree, isDescendant } from "@/lib/tree/tree";
import { getBuilderCanvasDocument } from "@/lib/canvas/helpers";
import {
  getNodeHtmlId,
  getClassNameForNode,
  getBaseClassNameForBuilderElement
} from "@/lib/elements/utils";
import { getNestingRuleFromElementSlug } from "@/lib/tree/nesting";
import { isDefined } from "ts-is-present";
import { generateDocumentStorePropertyChangeSnapshotLabel } from "@/lib/undo/snapshot-labels/document-store-property-change.utils";
import { SnapshotLabelAction } from "@/types/snapshot";
import { GLOBAL_SETTINGS_PATHS } from "@/store/modules/global";
import { DynamicFieldOutput } from "@/types/dynamic-data";
import { nl2br } from "@/util/nl2br";
import defensiveCopy from "@/util/defensive-copy";
import { v4 } from "uuid";
import { SsrNode } from "@/types/server-side-render";
import UIModule from "@/store/modules/ui";
import { deepEqual } from "@/util/deep-equal";
import { triggerShortcut } from "@/lib/keyboard-shortcuts/keyboard-shortcuts";

const addDependenciesToHeadDebounced = lodash.debounce(
  addBuilderElementDependenciesToHead,
  500
);

@Component({})
export default class BuilderElementComponent extends Vue {
  // TODO - this is a TreeNode. Not an element. Rename it to node.
  @Prop()
  element!: TreeNode;

  @Prop()
  parentId!: number;

  @Prop()
  ssrParentProperties?: PropertiesData;

  isActive = false;
  isUpdating = false;
  currentlyDragging = false;
  lastModifiedPropertyPath: string | null = null;

  /**
   * @see https://github.com/soflyy/breakdance/issues/4236
   */
  uniqueId = v4();

  get isEmptyContainer() {
    const elementNestingRuleType = getNestingRuleFromElementSlug(
      this.element.data.type
    ).type;

    const isContainer =
      elementNestingRuleType === "popup" ||
      elementNestingRuleType === "container" ||
      elementNestingRuleType === "container-link" ||
      elementNestingRuleType === "section";

    return isContainer && this.element.children.length === 0;
  }

  get isEmptyColumns() {
    return (
      this.element.data.type === COLUMNS_SLUG &&
      this.element.children.length === 0
    );
  }

  get parentProps() {
    const sharePropsWithSSRChildren =
      this.baseElement.settings &&
      this.baseElement.settings.sharePropsWithSSRChildren;

    // Intentionally ignore design props.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { design, ...props } = this.element.data.properties || {};

    // Share its own properties if it must, otherwise pass down the parent's
    // because we don't know if at some higher level the parent is sharing them
    return (sharePropsWithSSRChildren ? props : this.ssrParentProperties) || {};
  }

  __render(h: CreateElement) {
    const RootTag = this.rootTag;

    if (!this.shouldShow) {
      return false;
    }

    if (this.isEmptyColumns) {
      return (
        <RootTag
          {...{ attrs: { ...this.attributes } }}
          // @ts-ignore Because TS fails to infer possible attributes when JSX tag is dynamic expression
          onMousedown={this.activateAndMaybeStartDrag}
          onDragstart={this.disableDefaultDrag}
          onClick={this.maybePreventDefault}
          id={this.htmlId}
          class={this.classList}
        >
          <EmptyColumns
            // @ts-ignore
            id={this.element.id}
          />
        </RootTag>
      );
    }
    const innerHTML = renderTags(this.processedTemplate, {
      ssr: this.ssrValueOrMessage
    });

    const children = this.isEmptyContainer ? (
      <EmptyContainer
        // @ts-ignore
        id={this.element.id}
      />
    ) : (
      this.element.children.map(node => {
        return (
          <BuilderElementComponent
            // @ts-ignore
            key={node.id}
            // @ts-ignore
            element={node}
            parentId={this.element.id}
            ssrParentProperties={this.parentProps}
          />
        );
      })
    );

    const shouldRenderContentAsHtml =
      // content editable elements don't have children since their content is a string. Use their innerHTML directly
      this.contentEditablePropertyPath ||
      // SSR content should be replaced, as JS can modify an element in the html, and it won't be updated since the SSR didn't change that child.
      (this.ssrValueOrMessage !== null &&
        // except when it needs to render %%CHILDREN%%. We need to convert them into v nodes
        !this.ssrValueOrMessage.includes("%%CHILDREN%%"));

    const domProps: VNodeData["domProps"] = {};

    if (shouldRenderContentAsHtml) {
      domProps.innerHTML = innerHTML;
    }

    const arrayOfChildVNodes = shouldRenderContentAsHtml
      ? []
      : htmlToVNodes(
          innerHTML,
          h,
          /* %%CHILDREN%% in the HTML will be replaced with this jsx */
          () => children,
          {
            enableContentEditableAndFocus: this.enableContentEditableAndFocus,
            saveContentEditable: this.saveContentEditable
          }
        );

    return h(
      this.rootTag,
      {
        class: this.classList,
        attrs: {
          ...this.attributes,
          id: this.htmlId
        },
        domProps,
        on: {
          mousedown: this.activateAndMaybeStartDrag,
          dragstart: this.disableDefaultDrag,
          click: this.maybePreventDefault,
          dblclick: this.handleDoubleClick,
          blur: this.contentEditablePropertyPath
            ? this.saveContentEditable
            : () => {}
        }
      },
      arrayOfChildVNodes
    );
  }

  render(h: CreateElement) {
    return this.__render(h) as VNode;
  }

  get processedTemplate() {
    if (
      !this.hasRequiredPluginsAndTheyAreAvailable &&
      this.baseElement.settings &&
      this.baseElement.settings.requiredPlugins
    ) {
      return requiredPluginsNotAvailableMessage(
        this.baseElement.name,
        this.baseElement.settings.requiredPlugins
      );
    }

    const template = renderTwig(
      this.baseElement.template,
      this.twigProps,
      getBuilderElementTemplateCacheKey(this.baseElement)
    );

    if (!template) return "";

    return renderTags(template, this.tags);
  }

  get shouldSsr() {
    const templateHasSsr = this.processedTemplate.includes("%%SSR%%");
    if (!templateHasSsr) {
      return false;
    }

    // Bypass any other checks when refreshing SSR is explicitly requested
    if (this.refreshSsr) {
      return true;
    }

    // Don't check for allowed property paths when SSR is not triggered by a property change
    if (this.lastModifiedPropertyPath === null) {
      return true;
    }

    const propertyPathsToSsrElementWhenValueChanges =
      this.baseElement.propertyPathsToSsrElementWhenValueChanges;

    const thereIsNoWhitelist = !propertyPathsToSsrElementWhenValueChanges;
    if (thereIsNoWhitelist) {
      return true;
    }

    return propertyPathsToSsrElementWhenValueChanges.some(path =>
      // startsWith because a path can be for a sections to make all children SSR
      this.lastModifiedPropertyPath?.startsWith(path)
    );
  }

  get refreshSsr() {
    if (!uiStore.refreshSsrElementId) return false;

    return uiStore.refreshSsrElementId === this.element.id;
  }

  get hasRequiredPluginsAndTheyAreAvailable() {
    if (
      !this.baseElement.settings ||
      !this.baseElement.settings.requiredPlugins
    ) {
      return true;
    }

    const availablePlugins = this.baseElement.settings.requiredPlugins.filter(
      requiredPlugin =>
        configStore.availableWordpressPlugins.includes(requiredPlugin)
    );

    return (
      availablePlugins.length ===
      this.baseElement.settings.requiredPlugins.length
    );
  }

  get ssrValueOrMessage() {
    // An element with no ssr content may be updated by a non-ssr prop, so shouldSsr is false
    if (this.ssrInnerHtml === "") {
      if (uiStore.settings.disableNoticeForEmptySSRElements) return "";

      return emptyHtmlFromSsrMessage;
    }

    // Keep the SSR result if a non-SSR property is changed
    if (!this.shouldSsr && !this.ssrInnerHtml) return null;

    if (this.isUpdating) {
      return this.ssrInnerHtml || "";
    }

    return this.ssrInnerHtml;
  }

  get tags() {
    return {
      id: this.element.id,
      selector: this.elementSelector,
      uniqueSlug: this.uniqueClassName,
      postId: this.postId,
      elementName: this.baseElement.name
    };
  }
  ssrInnerHtml: null | string = null;

  oldSsrParentProperties = defensiveCopy(this.ssrParentProperties);

  @Watch("ssrParentProperties", {
    deep: true
  })
  async rerenderFromServeOnParentPropertyChange() {
    if (!this.shouldSsr) return;

    // Prop object references change on every render
    // So we can't rely on @Watch without comparing if the value actually changed
    // Use state for old value, since @Watch newValue/oldValue params are the same for props
    // Details & video explanation: https://github.com/soflyy/breakdance/pull/3856
    if (!deepEqual(this.ssrParentProperties, this.oldSsrParentProperties)) {
      this.oldSsrParentProperties = defensiveCopy(this.ssrParentProperties);
      await this.maybeRerenderFromServer();

      // Why are we not calling "runActionsAfterSsr" here? Is that on purpose?
    }
  }

  @Watch("refreshSsr")
  async rerenderFromServerOnRefreshSsr() {
    if (this.refreshSsr) {
      await this.maybeRerenderFromServer();

      uiStore.setRefreshSsrElementId(null);

      this.runActionsAfterSsr();
    }
  }

  oldTwigProps = defensiveCopy(this.element.data.properties);
  @Watch("twigProps")
  async maybeRerenderFromServerOnPropertyChange() {
    /**
     * Since this watcher may be called before other watchers, we have to wait
     * for the next tick, because we need other watchers to update state first,
     * so {@link shouldSsr()} would use up-to-date values in its logic.
     *
     * E.g. {@link lastModifiedPropertyPath} is set by the
     * {@link handleLastModifiedTreeNodePropertyChange} watcher function,
     * which is called by Vue after this function when a property changes.
     */
    await this.$nextTick();

    if (!this.shouldSsr) {
      // An element can have conditional SSR. Clean the SSR result if it's no longer applicable
      if (!this.processedTemplate.includes("%%SSR%%")) {
        // This condition is mandatory in order to avoid redundant re-rendering
        if (this.ssrInnerHtml !== null) {
          this.ssrInnerHtml = null;
        }
        /**
         * TODO this call entirely or some of its sub-calls may be redundant,
         * it used to be a part of a ditched "ssrInnerHtml" watcher function,
         * and was preserved for a potential backwards compatibility.
         */
        this.updateSsrDependenciesAndStyles();
        // TODO shouldn't we also clean SSR dependencies and styles?
      }

      return;
    }

    /**
     * When switching previews, "dynProps" will rerun, causing twigProps to change in reference but not value
     * If we rerun SSR at this point, it'll be
     *
     * 1) useless since no properties changed and
     * 2) bug prone because actions (like onMount) can run before the element finished rendering
     *
     * This also prevents useless re-renders when mounting an ssr element
     *
     * Explanation: https://github.com/soflyy/breakdance/pull/4290
     */
    if (deepEqual(this.twigProps, this.oldTwigProps)) {
      return;
    }

    this.oldTwigProps = this.twigProps;
    await this.maybeRerenderFromServer();

    this.runActionsAfterSsr();
  }

  runActionsAfterSsr() {
    breakdanceStoreActions.run.onBeforeDeletingElement({
      elementId: this.element.id
    });

    if (this.element._parentId) {
      breakdanceStoreActions.run.onAfterDeletedElement({
        deletedElementNodeType: this.element.data.type,
        parentIdOfDeletedElement: this.element._parentId,
        transactionKey: undefined
      });
    }

    breakdanceStoreActions.run.onMountedElement({
      elementId: this.element.id
    });
  }

  async maybeRerenderFromServer() {
    if (!this.shouldSsr) {
      return;
    }

    uiStore.setElementUpdatingState({
      elementId: this.element.id,
      isUpdating: true
    });

    const ssrNode = pipe(
      await serverSideRender(
        this.baseElement.slug,
        this.element.data.properties,
        this.element.id,
        this.parentProps
      ),
      fold(
        errors => {
          errors.forEach(error => {
            NotificationLogger.log.message(error.msg);
          });

          return {
            html: ssrErrorMessage,
            postsGeneratedCssFilePaths: {},
            dependencies: {}
          };
        },
        ssrNode => ssrNode
      )
    );

    uiStore.setElementUpdatingState({
      elementId: this.element.id,
      isUpdating: false
    });

    this.ssrInnerHtml = ssrNode.html;
    this.updateSsrDependenciesAndStyles(
      ssrNode.dependencies,
      ssrNode.postsGeneratedCssFilePaths
    );
  }

  maybeRerunAllScriptsInCanvasDocumentBody() {
    const dependsOnGlobalScripts =
      this.baseElement.settings &&
      this.baseElement.settings.dependsOnGlobalScripts;

    if (dependsOnGlobalScripts) {
      rerunAllScriptsInCanvasDocumentBody();
    }
  }

  /**
   * This cannot be inside a "ssrInnerHtml" watcher:
   * ssrInnerHtml value may remain intact after re-render,
   * but the contents of CSS files may change.
   */
  updateSsrDependenciesAndStyles(
    dependencies?: SsrNode["dependencies"],
    postsGeneratedCssFilePaths?: SsrNode["postsGeneratedCssFilePaths"]
  ) {
    this.maybeRerunAllScriptsInCanvasDocumentBody();

    if (dependencies) {
      addBuilderElementDependenciesToHead(getBuilderCanvasDocument(), {
        dependencies: [dependencies],
        // An element (like Block elements) can be added multiple times with scripts that need to rerun
        addInlineScriptsAsUnique: false,
        element: this.element
      });
    }

    addOrUpdateElementSubStylesComingFromSsr(
      getBuilderCanvasDocument(),
      this.element.id,
      postsGeneratedCssFilePaths
    );
  }

  get shouldShow() {
    return applyFilters(
      "elementShouldShow",
      true,
      this.element.data.properties,
      {
        currentBreakpoint: uiStore.currentBreakpoint
        // Add any data you want to share with filters here
      }
    );
  }

  get breakpoints() {
    return configStore.breakpoints;
  }

  // Update the styles if a breakpoint changes
  // E.G: a breakpoint value was updated or the user switched breakpoint types
  @Watch("breakpoints", { deep: true })
  onBreakpointsChange() {
    this.generateElementsCSS();
  }

  @Watch("twigProps", { deep: true, immediate: true })
  onTwigPropsChange() {
    this.generateElementsCSS();
  }

  //--- These values can change through Element Studio
  @Watch("baseElement.cssTemplate")
  onCssTemplateChange() {
    this.generateElementsCSS();
  }

  @Watch("baseElement.defaultCss")
  onDefaultCssChange() {
    this.generateElementsCSS();
  }

  @Watch("baseElement.dependencies", { deep: true })
  onInternalDependenciesChange() {
    if (this.baseElement.dependencies) {
      // Don't run internal scripts to avoid errors if the code is being saved before it's ready
      // Let the user execute manually
      const internalDependenciesWithoutInlineScripts =
        this.baseElement.dependencies.map(deps => ({
          ...deps,
          inlineScripts: []
        }));

      addDependenciesToHeadDebounced(getBuilderCanvasDocument(), {
        dependencies: internalDependenciesWithoutInlineScripts,
        tags: this.tags,
        element: this.element
      });
    }
  }
  //---

  //---
  get globalSettings() {
    return globalStore.globalSettings;
  }

  /**
   * For typography macro presets, we only render the values if they're set (so only add font-weight if it's set in the preset)
   * But the element is not tied to the global settings in any way (aka, no reactivity)
   * so a change in global settings won't cause a re-render without this
   *
   * Downside: a change in Global Settings will re-render *all* elements, even ones without typography or presets.
   * If this becomes too slow, we should find a way to know if an element has a typography preset set.
   */
  @Watch(`globalSettings.${GLOBAL_SETTINGS_PATHS.typographyPresets}`, {
    deep: true
  })
  refreshIfTypographyPresetsChange() {
    this.generateElementsCSS();
  }

  get elementChildrenCount() {
    return this.element.children.length;
  }

  @Watch("elementChildrenCount", {})
  elementChildrenCountChanged() {
    this.generateElementsCSS();
  }

  generateElementsCSS() {
    const css = generateCss(
      this.twigProps,
      this.breakpoints,
      this.baseElement.cssTemplate,
      this.tags,
      this.baseElement.propertyPathsToWhitelistInFlatProps,
      this.elementChildrenCount,
      getBuilderElementCssTemplateCacheKey(this.baseElement)
    );

    // default CSS first to make the element's CSS (the func below) higher precedence
    addOrUpdateGeneratedBuilderElementDefaultStylesInlineCSSInCanvasHead(
      getBuilderCanvasDocument(),
      this.baseElement.slug,
      this.baseElement.defaultCss
    );

    addOrUpdateGeneratedBuilderElementStylesInlineCSSInCanvasHead(
      getBuilderCanvasDocument(),
      this.element.id,
      css,
      this.uniqueId
    );
  }

  async mounted() {
    /**
     * TODO this call entirely or some of its sub-calls might be redundant.
     * It used to be a part of a ditched "ssrInnerHtml" watcher function,
     * which was "immediate" and fired before mount,
     * so this call was preserved for backwards compatibility.
     * @see https://github.com/soflyy/breakdance/pull/4560/files#r1013915111
     */
    this.updateSsrDependenciesAndStyles();

    // Run both method in parallel
    await Promise.all([
      this.loadDependencies(),
      this.maybeRerenderFromServer()
    ]);

    // Run onMountedElement action after dependencies and SSR are loaded
    breakdanceStoreActions.run.onMountedElement({
      elementId: this.element.id
    });
  }

  destroyed() {
    const areThereNoOtherElementsOfThisType =
      countNodesOfTypeInTree(
        documentStore.document.tree.root,
        this.baseElement.slug
      ) === 0;

    if (areThereNoOtherElementsOfThisType) {
      removeElementDefaultStylesFromCanvasHead(
        getBuilderCanvasDocument(),
        this.baseElement.slug
      );
    }

    removeElementStylesFromCanvasHead(
      getBuilderCanvasDocument(),
      this.element.id,
      this.uniqueId
    );

    removeElementStylesCameFromSsrFromCanvasHead(
      getBuilderCanvasDocument(),
      this.element.id
    );

    if (this.element._parentId) {
      breakdanceStoreActions.run.onAfterDeletedElement({
        deletedElementNodeType: this.element.data.type,
        parentIdOfDeletedElement: this.element._parentId,
        transactionKey: `DocumentDeleteElement:ID:${this.element.id}`
      });
    }
  }

  loadDependencies() {
    return addBuilderElementDependenciesToHead(getBuilderCanvasDocument(), {
      dependencies: [
        ...(this.baseElement.dependencies || []),
        ...(configStore.elementsExternalAttrsDepsAndActions.dependencies || [])
      ],
      element: this.element,
      tags: this.tags
    });
  }

  get elementSelector() {
    return `.breakdance .${this.uniqueClassName}`;
  }

  get rootTag(): string {
    const customTag: unknown = this.baseElement.htmlTag.pathToControl
      ? lodash.get(
          this.element.data.properties,
          this.baseElement.htmlTag.pathToControl
        )
      : this.element.data.properties?.settings?.advanced?.tag;

    if (typeof customTag === "string")
      return customTag || this.baseElement.htmlTag.default;

    return this.baseElement.htmlTag.default;
  }

  get baseElement() {
    return configStore.element(this.element.data.type);
  }

  get twigProps(): object {
    if (
      isObject(this.element.data.properties) &&
      Object.entries(this.element.data.properties).length !== 0
    ) {
      const cachedDynamicData = configStore.dynamicDataCache.data;

      const dynamicShortcodes = getDynamicShortcodesFromPropertiesData(
        this.element.data.properties,
        cachedDynamicData
      );

      const uncachedDynamicShortcodes = dynamicShortcodes.filter(
        dynamicShortcode =>
          !Object.keys(cachedDynamicData).includes(hashDynamicShortcode(dynamicShortcode))
      );

      // Request new dynamic values if they're not in the store (e.g. the element was added)
      if (uncachedDynamicShortcodes.length) {
        loadDynamicData(uncachedDynamicShortcodes).then(newDynamicData => {
          configStore.setNewDynamicDataCacheEntries(newDynamicData);
        });
      }

      const isThereSomethingToPreview = !!configStore.previewedItem;

      return objectDeepMap(this.element.data.properties, (value: unknown) => {
        if (isDynamicShortcode(value)) {
          /*
          if there is nothing to preview (i.e. preview dropdown is in an error state)
          we show the raw shortcode.

          if there is something to preview, then dynamic data is working, and
          if a dynamic data point really is blank/undefined/false/whatever...
          then we just let it be blank. we don't return the raw shortcode.
          we only return the raw shortcode when the preview dropdown is in an error state.

          relevant explanation / rationale / thoughts: https://github.com/soflyy/breakdance/issues/349#issuecomment-882815000
          */
          if (cachedDynamicData[hashDynamicShortcode(value)] || isThereSomethingToPreview) {
            return cachedDynamicData[hashDynamicShortcode(value)];
          }
          return value;
        }

        return value;
      }) as object;
    }

    return {};
  }

  get postId() {
    return documentStore.document.documentMeta.id;
  }

  get htmlId() {
    return getNodeHtmlId(this.element);
  }

  get uniqueClassName() {
    return getClassNameForNode(this.element, this.postId);
  }

  get baseClassName() {
    return getBaseClassNameForBuilderElement(this.baseElement);
  }

  get classList() {
    const classes = [
      this.uniqueClassName,
      "builder-element",
      this.baseClassName
    ];

    if (this.isActive) {
      classes.push("breakdance--active-element");
    }

    if (this.currentlyDragging) {
      classes.push("breakdance--currently-dragging");
    }

    if (this.isEmptyContainer) {
      classes.push("breakdance--empty-container");
    }

    const classList = (this.element.data.properties?.settings?.advanced
      ?.classes ?? false) as false | SelectorCSSName[];

    if (classList) {
      classes.push(...classList);
    }

    if (
      this.baseElement.settings &&
      this.baseElement.settings.bypassPointerEvents
    ) {
      classes.push("breakdance-children-pointer-events-none");
    }

    if (this.isUpdating) {
      classes.push("breakdance-element-is-updating");
    }

    classes.push(...this.additionalClasses);

    return classes;
  }

  get additionalClasses(): string[] {
    if (!this.baseElement.additionalClasses) return [];
    return this.baseElement.additionalClasses
      .map(classRule => {
        const template = renderTwig(
          classRule.template,
          this.twigProps,
          getBuilderElementAdditionalClassTemplateCacheKey(
            this.baseElement,
            classRule
          )
        );

        if (!template) return;

        return {
          name: classRule.name,
          include: template
        };
      })
      .filter(isDefined)
      .filter(x => !!x.include)
      .map(x => x.name);
  }

  renderAttributeValue(attribute: BuilderElementAttribute): string | null {
    if ("template" in attribute) {
      return renderTwig(
        attribute.template,
        this.twigProps,
        getBuilderElementAttributeTemplateCacheKey(this.baseElement, attribute)
      );
    }
    if ("propertyPath" in attribute) {
      return String(
        lodash.get(this.element.data.properties, attribute.propertyPath, "")
      );
    }
    if ("rawValue" in attribute) {
      return attribute.rawValue;
    }

    return null;
  }

  get attributes() {
    type attrs = {
      [key: string]: string | undefined;
    };
    const atts: attrs = {};

    /* attribute rules defined by the element */
    const attributes = [
      ...(this.baseElement.attributes || []),
      ...(configStore.elementsExternalAttrsDepsAndActions.attributes || [])
    ];

    attributes.forEach(rule => {
      const value = this.renderAttributeValue(rule);

      if (value) {
        atts[rule.name] = renderTags(value, this.tags);
      }
    });

    type Attribute = { name: string; value: string };

    /* user attribute settings at settings. this might make more sense to code in a plugin and run with a hook */
    const userAttSettings = (this.element.data.properties?.settings?.advanced
      ?.attributes ?? []) as Attribute[];

    userAttSettings.forEach((userAtt: Attribute) => {
      atts[userAtt.name] = userAtt.value;
    });

    atts["data-node-id"] = `${this.element.id}`;

    // remove blank attributes.
    const cleanAtts: attrs = {};

    Object.keys(atts).forEach(key => {
      if (atts[key]) {
        cleanAtts[key] = atts[key];
      }
    });

    return cleanAtts;
  }

  get contentEditablePropertyPath() {
    return this.attributes["data-content-editable-property-path"];
  }

  get isDragAndDropEnabled() {
    return uiStore.hasFullAccess;
  }

  /**
   * @deprecated It's not deprecated, but do not use this computed property directly!
   */
  get lastModifiedTreeNodePropertyForWatcher() {
    return uiStore.lastModifiedElementProperty;
  }

  @Watch("lastModifiedTreeNodePropertyForWatcher", { immediate: true })
  handleLastModifiedTreeNodePropertyChange(
    lastModified: UIModule["lastModifiedElementProperty"]
  ) {
    if (lastModified.elementId === this.element.id) {
      this.lastModifiedPropertyPath = lastModified.path;
    } else if (this.lastModifiedPropertyPath !== null) {
      this.lastModifiedPropertyPath = null;
    }
  }

  /**
   * @deprecated It's not deprecated, but do not use this computed property directly, use this.currentlyDragging instead!
   */
  get currentlyDraggingForWatcher() {
    return uiStore.currentlyDraggingCanvasElementId === this.element.id;
  }

  /**
   * If currentlyDragging was a computed property, any change made to
   * the uiStore.currentlyDraggingCanvasElementId would cause a complete tree re-render.
   */
  @Watch("currentlyDraggingForWatcher", { immediate: true })
  handleCurrentlyDraggingChange(currentlyDragging: boolean) {
    this.currentlyDragging = currentlyDragging;
  }

  /**
   * @deprecated It's not deprecated, but do not use this computed property directly, use this.isActive instead!
   */
  get activeElementIdForWatcher() {
    return uiStore.activeElementId;
  }

  /**
   * If isActive was a computed property, any change made to
   * the uiStore.activeElementId would cause a complete tree re-render.
   */
  @Watch("activeElementIdForWatcher", { immediate: true })
  handleActiveElementIdChange(activeElementId: UIModule["activeElementId"]) {
    this.isActive = activeElementId === this.element.id;
  }

  /**
   * @deprecated It's not deprecated, but do not use this computed property directly, use this.isUpdating instead!
   */
  get isElementUpdatingForWatcher() {
    return Boolean(uiStore.elementsThatAreUpdating[this.element.id]);
  }

  /**
   * If isActive was a computed property, any change made to
   * the uiStore.elementsThatAreUpdating would cause a complete tree re-render.
   */
  @Watch("isElementUpdatingForWatcher", { immediate: true })
  handleLoadingElementIdChange(isUpdating: boolean) {
    if (this.isUpdating !== isUpdating) {
      this.isUpdating = isUpdating;
    }
  }

  get behaviorModeEnabled() {
    const builderBehaviorId = uiStore.builderBehaviorElementId;

    if (builderBehaviorId) {
      return isDescendant(
        documentStore.document.tree,
        builderBehaviorId,
        this.element.id
      );
    }

    return false;
  }

  /* drag logic */
  dragMouseStartX: false | number = false;
  dragMouseStartY: false | number = false;

  easyEvent = new EasyEvent();

  activateElement() {
    if (this.behaviorModeEnabled) return;

    uiStore.activateElement(this.element.id);
    openUiPanel("elementproperties");
  }

  canDrag(e: MouseEvent) {
    if (!this.isDragAndDropEnabled) return false;

    const target = e.target as HTMLElement;
    const elementsToIgnore = ["textarea", "input", "select"];
    const currentElement = target.tagName.toLowerCase();

    // disable dragging if the current element should be ignored.
    if (elementsToIgnore.includes(currentElement)) return false;

    // html content editable relies on mousedown events
    if (uiStore.elementWithContentEditable) return false;

    return true;
  }

  disableDefaultDrag(e: MouseEvent) {
    // Prevents the default html-draggable event from happening
    e.preventDefault();
  }

  maybePreventDefault(event: Event) {
    /*
     * Prevent:
     * 1. Following links in the Builder
     * 2. Buttons from submitting forms.
     */

    const target = event.target as HTMLElement;
    const elementsToPrevent = ["a", "button"];
    const selector = elementsToPrevent.join(", ");
    const isAChild = elementsToPrevent.some(el => target.closest(el));

    if (target.matches(selector) || isAChild) {
      event.preventDefault();
    }
  }

  activateAndMaybeStartDrag(e: MouseEvent) {
    e.stopPropagation();

    this.activateElement();

    if (!this.canDrag(e)) return;
    if (this.behaviorModeEnabled) return;

    this.dragMouseStartX = e.clientX;
    this.dragMouseStartY = e.clientY;

    this.easyEvent.on("mousemove", this.dragging);
    this.easyEvent.on("mouseup", this.removeDragListeners);
  }

  removeDragListeners() {
    this.easyEvent.removeAll();

    this.dragMouseStartX = false;
    this.dragMouseStartY = false;
  }

  dragging(e: MouseEvent) {
    if (this.behaviorModeEnabled) return;
    if (!isCanvasEvent(e)) return;

    if (this.dragMouseStartX === false || this.dragMouseStartY === false)
      return;

    const mouseX = e.clientX;
    const mouseY = e.clientY;

    if (
      Math.abs(mouseX - this.dragMouseStartX) > 3 ||
      Math.abs(mouseY - this.dragMouseStartY) > 3
    ) {
      uiStore.setCanvasElementDraggedId(this.element.id);
      this.removeDragListeners();
    }
  }

  /* End drag logic */

  handleDoubleClick(e: Event) {
    if (this.contentEditablePropertyPath) {
      this.enableContentEditableAndFocus(e);
    }
    // open rich text editor modal on double click
    else if (this.element.data.type === RICH_TEXT_SLUG) {
      triggerShortcut("CMD_E");
    }
  }

  /* ContentEditable logic */

  // On double click
  enableContentEditableAndFocus(e: Event) {
    const target: HTMLElement = e.target as HTMLElement;
    let editableElement = null;

    // the element itself
    if (target.dataset.contentEditablePropertyPath || target.dataset.nodeId) {
      editableElement = target;
    }
    // Or the closest parent that has a "data-content-editable-property-path" property
    // (the click can happen in a child that isn't content editable)
    else {
      editableElement = target.closest(
        "[data-content-editable-property-path]"
      ) as HTMLElement;
    }

    if (!editableElement) return;

    const contentEditablePropertyPath =
      editableElement.dataset.contentEditablePropertyPath;

    if (contentEditablePropertyPath) {
      const maybeDynamicData: DynamicFieldOutput | undefined = lodash.get(
        this.twigProps,
        getDynamicMetaPath(contentEditablePropertyPath)
      );

      const elementValueIsDynamic = maybeDynamicData?.field.slug;

      if (elementValueIsDynamic) {
        return;
      }
    }

    const elementIsAlreadyActive =
      editableElement.getAttribute("contenteditable") === "true";

    if (elementIsAlreadyActive) return;

    uiStore.setContentEditableElementId(this.element.id);

    editableElement.setAttribute("contenteditable", "true");
    editableElement.focus();

    window.Breakdance.canvas.document.addEventListener(
      "keydown",
      this.handleKeydownInsideContentEditable
    );

    editableElement.addEventListener("paste", this.pasteWithoutFormatting);
  }

  // on blur
  saveContentEditable(e: Event) {
    const target = e.target as HTMLElement;

    window.Breakdance.canvas.document.removeEventListener(
      "keydown",
      this.handleKeydownInsideContentEditable
    );

    target.removeEventListener("paste", this.pasteWithoutFormatting);

    target.setAttribute("contenteditable", "false");

    uiStore.setContentEditableElementId(null);

    const path =
      target.dataset.contentEditablePropertyPath ||
      this.contentEditablePropertyPath;

    if (!path) {
      return;
    }

    target.innerHTML = target.innerHTML.trim();

    // Remove the last <br> tag if it's present. Some browsers (firefox) add it
    const lastElement = target.childNodes[target.childNodes.length - 1] as
      | HTMLElement
      | undefined;

    if (lastElement && lastElement.tagName === "BR") {
      lastElement.remove();
    }

    // save decoded HTML to prevent stuff like & becoming &amp
    const textAreaWithTargetInnerHtml = document.createElement("textarea");
    textAreaWithTargetInnerHtml.innerHTML = target.innerHTML;
    const decodedHtml = textAreaWithTargetInnerHtml.value;

    // Don't save if the value didn't change
    const prevValue: unknown = lodash.get(this.element.data.properties, path);

    if (prevValue === decodedHtml) return;

    documentStore.throttledPropertyChanged({
      elementId: this.element.id,
      path,
      value: decodedHtml,
      meta: {
        snapshotLabel:
          generateDocumentStorePropertyChangeSnapshotLabel.forElementBasingOnChangedStorePropertyPath(
            this.baseElement,
            path,
            SnapshotLabelAction.CHANGE
          )
      }
    });
  }

  // Force pasted text to be without formatting (colors, bold, etc)
  // https://developer.mozilla.org/en-US/docs/Web/API/Element/paste_event#live_example
  pasteWithoutFormatting(event: ClipboardEvent) {
    const paste = event.clipboardData?.getData("text/plain");
    if (!paste) return;

    window.Breakdance.canvas.document.execCommand(
      "insertHTML",
      false,
      // since we get the pasted data as plain text, and HTML renders new lines as just space, we turn them into BR
      // to get the same behaviour as if the user had pressed enter
      nl2br(paste)
    );

    // prevent at the end, in case "getData" failed
    event.preventDefault();
  }

  handleKeydownInsideContentEditable(event: KeyboardEvent) {
    // Pressing Space doesn't always work inside content editable.
    // https://stackoverflow.com/q/48512479
    if (event.code === "Space") {
      event.preventDefault();
      event.stopPropagation();
      window.Breakdance.canvas.document.execCommand("insertText", false, " ");
      return;
    }

    /**
     * add line break on enter. This makes all browsers behave the same, instead of some of them adding it and some not
     * https://stackoverflow.com/a/61237402/5993042
     * "execCommand" is deprecated but "insertLineBreak" works fine on all big browsers and knowing the web, it'll never
     * be removed due to backwards compatibility.
     */
    if (event.code === "Enter") {
      window.Breakdance.canvas.document.execCommand("insertLineBreak");
      event.preventDefault();
      return;
    }

    if (event.code === "Escape") {
      event.preventDefault();
      this.saveContentEditable(event);
      return;
    }

    // prevent the "save" dialog when inside content editable. Our global shortcuts don't apply here for some reason
    // instead make it "save" the changes in the content editable
    if ((event.ctrlKey || event.metaKey) && event.key === "s") {
      event.preventDefault();
      this.saveContentEditable(event);
      return;
    }
  }
}
